import { piecePower } from '../data/biome-sets.js';
import { powerLevelOf } from '../data/raid-gear.js';
import { BIOME_IDS } from '../data/biomes.js';
import { AFFIX_COUNT, AFFIX_IDS, AFFIXES, REFORGE_GOLD_FACTOR, SEAL_RELICS, RUNESMITH_REACH, } from '../data/affixes.js';
import { rarityRank } from '../data/items.js';
import { originBiome, UPGRADE_BASE_COST, upgradeMaterialOf } from '../data/upgrades.js';
import { CRIT_CHANCE, CRIT_MULTIPLIER } from './combat.js';
import { roundDps } from './dps.js';
import { ENERGY_PER_SECOND } from './spells.js';
import { isUpgradeable } from './upgrade.js';
/**
 * Item affixes (ADR 0057): which pieces carry them, how they roll, what the
 * sum of everything worn gives, and what the runesmith asks to reroll them.
 * Pure; the server rolls and applies, the client shows the same numbers.
 */
const AFFIX_SLOTS = new Set([
    'helmet',
    'chest',
    'gloves',
    'boots',
    'weapon',
    'offhand',
]);
/**
 * Whether a piece carries affixes at all: found gear of a rarity above common,
 * from a biome's pool or from Shefira (`source: 'raid'`). Never a quest's
 * reward, a cosmetic, a trophy or a shop item — none of them is `lootable` —
 * nor a companion.
 */
export function isAffixable(template) {
    const found = template.lootable || template.source === 'raid';
    if (!found || template.cosmetic)
        return false;
    if (template.slot === undefined || !AFFIX_SLOTS.has(template.slot))
        return false;
    return AFFIX_COUNT[template.rarity] > 0 && isUpgradeable(template);
}
export function affixCountOf(template) {
    return isAffixable(template) ? AFFIX_COUNT[template.rarity] : 0;
}
/** The affixes this piece's slot allows, in catalogue order. */
export function affixesAllowed(template) {
    const slot = template.slot;
    if (slot === undefined || !AFFIX_SLOTS.has(slot))
        return [];
    return AFFIX_IDS.filter((id) => AFFIXES[id].slots.includes(slot));
}
/** The power a piece's `power` affixes are measured in: its stats' own (`piecePower`). */
export function affixPowerOf(template) {
    return piecePower(powerLevelOf(template), template.rarity, rarityRank(template.rarity));
}
function toStep(value, step) {
    return step === 1 ? Math.round(value) : roundDps(value);
}
/**
 * Bounds of an affix on a piece of `power`, in its unit. A `power` affix is
 * never below one step, so a meadow rare still gets something.
 */
export function affixRange(id, power) {
    const { scale, min, max, step } = AFFIXES[id];
    const factor = scale === 'power' ? power : 1;
    const low = Math.max(step, toStep(min * factor, step));
    return { min: low, max: Math.max(low, toStep(max * factor, step)) };
}
/** One value, uniform over the steps of its range. */
export function rollAffixValue(id, power, rng) {
    const { step } = AFFIXES[id];
    const { min, max } = affixRange(id, power);
    const steps = Math.round((max - min) / step);
    const pick = Math.min(steps, Math.floor(rng() * (steps + 1)));
    return toStep(min + pick * step, step);
}
function inCatalogueOrder(affixes) {
    return affixes.sort((a, b) => AFFIX_IDS.indexOf(a.id) - AFFIX_IDS.indexOf(b.id));
}
/**
 * A piece's affixes: those in `kept` as they are, and as many new ones as
 * its rarity asks for, drawn without replacement among what its slot allows
 * and `kept` does not already hold. At the drop `kept` is empty; at the
 * runesmith it is what the player locked.
 */
export function rollAffixes(template, rng, kept = []) {
    const count = affixCountOf(template);
    if (count === 0)
        return [];
    const result = kept.slice(0, count).map((affix) => ({ ...affix }));
    const pool = affixesAllowed(template).filter((id) => !result.some((affix) => affix.id === id));
    const power = affixPowerOf(template);
    while (result.length < count && pool.length > 0) {
        const [id] = pool.splice(Math.floor(rng() * pool.length), 1);
        if (id === undefined)
            break;
        result.push({ id, value: rollAffixValue(id, power, rng) });
    }
    return inCatalogueOrder(result);
}
/** Whether a stored list fits its piece: allowed ids, values within range, the right count. */
export function affixesFit(template, affixes) {
    if (affixes.length !== affixCountOf(template))
        return false;
    const allowed = affixesAllowed(template);
    const power = affixPowerOf(template);
    return affixes.every((affix) => {
        const { min, max } = affixRange(affix.id, power);
        return allowed.includes(affix.id) && affix.value >= min - 1e-9 && affix.value <= max + 1e-9;
    });
}
/** Sum of each affix over everything worn, before any ceiling. */
export function affixTotals(lists) {
    const totals = Object.fromEntries(AFFIX_IDS.map((id) => [id, 0]));
    for (const list of lists) {
        for (const affix of list ?? [])
            totals[affix.id] = roundDps(totals[affix.id] + affix.value);
    }
    return totals;
}
/** What of a total counts: all of it, up to the affix's ceiling. */
export function cappedTotal(id, total) {
    const cap = AFFIXES[id].cap;
    return cap === undefined ? total : Math.min(cap, total);
}
/** The three stats one piece's affixes add to its own (ADR 0015 stays whole). */
export function affixStatBonus(affixes) {
    const bonus = { health: 0, defence: 0, dps: 0 };
    for (const affix of affixes ?? []) {
        if (affix.id === 'dps')
            bonus.dps = roundDps(bonus.dps + affix.value);
        else if (affix.id === 'health')
            bonus.health += affix.value;
        else if (affix.id === 'defence')
            bonus.defence += affix.value;
    }
    return bonus;
}
export const BASE_COMBAT_STATS = Object.freeze({
    critChance: CRIT_CHANCE,
    critMultiplier: CRIT_MULTIPLIER,
    cooldownReduction: 0,
    energyPerSecond: ENERGY_PER_SECOND,
    lifesteal: 0,
    bossDamage: 0,
});
export function combatStatsOf(lists) {
    const totals = affixTotals(lists);
    const counted = (id) => cappedTotal(id, totals[id]);
    return {
        critChance: CRIT_CHANCE + counted('crit-chance') / 100,
        critMultiplier: CRIT_MULTIPLIER + counted('crit-damage') / 100,
        cooldownReduction: counted('cooldown') / 100,
        energyPerSecond: roundDps(ENERGY_PER_SECOND + counted('energy')),
        lifesteal: counted('lifesteal') / 100,
        bossDamage: counted('boss-damage') / 100,
    };
}
/**
 * The damage per second a character deals on average, crits included: what
 * the sheet shows under « DPS ». The blow itself still deals `dps` and rolls
 * its crit, so this is a display and never counted twice.
 */
export function effectiveDps(dps, combat = BASE_COMBAT_STATS) {
    return roundDps(dps * (1 + combat.critChance * (combat.critMultiplier - 1)));
}
/** The same against a boss or an elite. */
export function bossDps(dps, combat = BASE_COMBAT_STATS) {
    return roundDps(effectiveDps(dps, combat) * (1 + combat.bossDamage));
}
/** Whether `boss-damage` applies to this creature: a boss of any rank, or an elite. */
export function isBossOrElite(mob) {
    return mob.rank !== 'ordinary' || mob.rarity !== 'common';
}
/** A blow against `mob`, before the crit and the creature's defence. */
export function bossDamageHit(amount, mob, combat) {
    if (amount <= 0 || combat.bossDamage <= 0 || !isBossOrElite(mob))
        return amount;
    return Math.round(amount * (1 + combat.bossDamage));
}
/**
 * Health a blow of `dealt` damage gives back, and the fraction left over for
 * the next one: 2 % of 30 is 0.6, and three such blows must still heal a
 * point and more.
 */
export function lifestealHeal(dealt, lifesteal, carry) {
    if (dealt <= 0 || lifesteal <= 0)
        return { heal: 0, carry };
    const total = carry + dealt * lifesteal;
    const heal = Math.floor(total + 1e-9);
    return { heal, carry: total - heal };
}
/** A spell's cooldown once the affixes have shortened it. */
export function reducedCooldown(cooldownMs, combat) {
    return Math.round(cooldownMs * (1 - combat.cooldownReduction));
}
/**
 * A reroll at the runesmith: gold, one rare material of every biome from the
 * meadow to the piece's own, one of the piece's relics per affix whose kind
 * is kept this time (locked whole, or kept for a new value), and
 * `SEAL_RELICS` per affix sealed now. An affix sealed before costs nothing.
 * The same price every time.
 */
export function reforgeCost(template, kept, seals = 0) {
    if (!isAffixable(template))
        return undefined;
    const biome = originBiome(template);
    const reached = BIOME_IDS.slice(0, BIOME_IDS.indexOf(biome) + 1);
    const materials = reached.map((id) => ({
        templateId: upgradeMaterialOf(id, 'rare'),
        quantity: 1,
    }));
    const relics = kept + seals * SEAL_RELICS;
    if (relics > 0)
        materials.push({ templateId: upgradeMaterialOf(biome, 'epic'), quantity: relics });
    return { gold: UPGRADE_BASE_COST[biome] * REFORGE_GOLD_FACTOR, materials };
}
/**
 * Why this reroll cannot happen on this piece, or undefined when it can. An
 * affix is named once at most, never one already sealed, and at least one
 * affix must be drawn anew or given a new value: what is sealed, locked or
 * sealed now stays below the piece's count.
 */
export function reforgeRefusal(template, affixes, choices) {
    if (!isAffixable(template))
        return 'not-affixable';
    const { locks, values = [], seals = [] } = choices;
    const named = [...locks, ...values, ...seals];
    if (new Set(named).size !== named.length)
        return 'unknown-lock';
    if (named.some((id) => !affixes.some((affix) => affix.id === id)))
        return 'unknown-lock';
    if (named.some((id) => affixes.some((affix) => affix.id === id && affix.sealed === true))) {
        return 'sealed';
    }
    const sealedBefore = affixes.filter((affix) => affix.sealed === true).length;
    if (sealedBefore + locks.length + seals.length >= affixCountOf(template))
        return 'all-locked';
    return undefined;
}
/**
 * The runesmith's reroll: sealed and locked affixes kept as they are (those
 * in `seals` sealed from now on), those in `values` kept in kind with a new
 * value, the rest drawn anew among the kinds the piece does not keep.
 */
export function reforgeAffixes(template, affixes, choices, rng) {
    const { locks, values = [], seals = [] } = choices;
    const power = affixPowerOf(template);
    const kept = affixes.flatMap((affix) => {
        if (affix.sealed === true || locks.includes(affix.id))
            return [affix];
        if (seals.includes(affix.id))
            return [{ ...affix, sealed: true }];
        if (values.includes(affix.id)) {
            return [{ id: affix.id, value: rollAffixValue(affix.id, power, rng) }];
        }
        return [];
    });
    return rollAffixes(template, rng, kept);
}
/** How many affixes this reroll keeps in kind for a relic each, and how many it seals. */
export function reforgeRelicCounts(choices) {
    return {
        kept: choices.locks.length + (choices.values?.length ?? 0),
        seals: choices.seals?.length ?? 0,
    };
}
export function withinRunesmithReach(at, runesmiths) {
    return runesmiths.some((smith) => Math.max(Math.abs(smith.x - at.x), Math.abs(smith.y - at.y)) <= RUNESMITH_REACH);
}
function formatValue(value) {
    return Number.isInteger(value) ? String(value) : value.toFixed(1).replace('.', ',');
}
/** « +2 % de chance de critique »: how an affix reads everywhere. */
export function describeAffix(affix) {
    return AFFIXES[affix.id].label.replace('{v}', formatValue(affix.value));
}
/** « 1 à 3 »: the lowest and the highest roll of an affix on this piece. */
export function describeAffixRange(id, template) {
    const { min, max } = affixRange(id, affixPowerOf(template));
    return min === max ? formatValue(min) : `${formatValue(min)} à ${formatValue(max)}`;
}
/**
 * A reproducible seed from an instance id, for the one-off migration that
 * gives the pieces already in play their affixes: run twice on a copy of the
 * database, it draws the same thing (FNV-1a).
 */
export function affixSeed(itemId) {
    let hash = 0x811c9dc5;
    for (let i = 0; i < itemId.length; i += 1) {
        hash ^= itemId.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
    }
    return hash >>> 0;
}
//# sourceMappingURL=affixes.js.map